import { DestroyRef, inject, Injectable, NgZone } from '@angular/core';
import { DOCUMENT } from '@angular/common';

/**
 * Elements whose default Enter behaviour is useful and must survive the guard: text entry that
 * takes newlines, and controls Enter activates or opens.
 */
const ENTER_EXEMPT_SELECTOR = [
  'textarea',
  '[contenteditable]:not([contenteditable="false"])',
  'button',
  'a[href]',
  'summary',
  '[role="button"]',
  'select',
  'input[type="file"]',
  'input[type="color"]',
  'input[type="date"]',
  'input[type="time"]',
  'input[type="datetime-local"]',
  'input[type="month"]',
  'input[type="week"]',
].join(',');

const ALLOW_ENTER_SUBMIT_SELECTOR = '[data-allow-enter-submit]';

/** Keyboard event `keyCode` browsers report while an IME composition session is in progress. */
const IME_COMPOSITION_KEY_CODE = 229;

export function isEnterExempt(target: Element): boolean {
  return target.matches(ENTER_EXEMPT_SELECTOR);
}

/** An Enter press the user meant, rather than a key repeat or an IME committing a composition. */
export function isDeliberateEnter(event: KeyboardEvent): boolean {
  return event.key === 'Enter'
    && !event.repeat
    && !event.isComposing
    && event.keyCode !== IME_COMPOSITION_KEY_CODE;
}

/** The chord that saves: Ctrl+Enter, or Cmd+Enter on macOS, with no other modifier held. */
export function isSaveChord(event: KeyboardEvent): boolean {
  return isDeliberateEnter(event)
    && (event.ctrlKey || event.metaKey)
    && !event.altKey
    && !event.shiftKey;
}

/**
 * Angular renders nested `<form>` elements the HTML parser would have discarded, so a form can
 * have a form ancestor. Submitting the outermost one reaches every `FormGroupDirective` involved.
 */
export function outermostFormAncestor(form: HTMLFormElement): HTMLFormElement {
  let outermost = form;
  for (let parent = form.parentElement?.closest('form'); parent; parent = parent.parentElement?.closest('form')) {
    outermost = parent;
  }
  return outermost;
}

/**
 * Stops Enter from submitting forms application-wide, and makes Ctrl/Cmd+Enter submit instead.
 *
 * A single document-level listener rather than a directive: the application is standalone, so a
 * directive would have to be added to the `imports` of every component that hosts a form.
 *
 * The listener runs in the bubble phase and ignores events that are already `defaultPrevented`,
 * which is how components opt out implicitly. Angular Material calls `preventDefault()` whenever it
 * consumes Enter, so autocompletes, selects, datepickers and chip inputs keep working without this
 * service knowing anything about them.
 */
@Injectable({ providedIn: 'root' })
export class EnterKeyGuardService {
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  install(): void {
    const handle = (event: KeyboardEvent) => this.handleKeydown(event);
    // Registered outside Angular so ordinary typing does not trigger change detection.
    this.zone.runOutsideAngular(() => this.document.addEventListener('keydown', handle));
    this.destroyRef.onDestroy(() => this.document.removeEventListener('keydown', handle));
  }

  private handleKeydown(event: KeyboardEvent): void {
    // An already-prevented event has been handled by a component -- Material cancels every Enter it
    // consumes, and detail forms cancel the save chord they act on.
    if (!isDeliberateEnter(event) || event.defaultPrevented) {
      return;
    }

    const target = event.target instanceof Element ? event.target : null;
    const form = target?.closest('form');
    if (!form || target.closest(ALLOW_ENTER_SUBMIT_SELECTOR)) {
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      if (!isSaveChord(event)) {
        return;
      }
      event.preventDefault();
      this.zone.run(() => outermostFormAncestor(form)
        .dispatchEvent(new SubmitEvent('submit', { bubbles: true, cancelable: true })));
      return;
    }

    if (!isEnterExempt(target)) {
      event.preventDefault();
    }
  }
}
